import { forwardRef, memo, useMemo, type Ref, type ReactNode } from 'react';
import startsWith from 'lodash/startsWith';
import { Link as RouterLink } from 'react-router-dom';
import { useIntl } from 'react-intl';
// Material UI imports
import type { SxProps, Theme, TypographyVariant } from '@mui/material';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
// local imports
import { fontWeightBold, typography } from '../styles/themeOptions';
// SCSS imports
import { drawerItem } from './AppDrawerItem.module.scss';

const slotProps = {
  primary: {
    variant: 'subtitle2' as TypographyVariant,
    color: 'secondary',
    className: drawerItem
  }
};

const iconSx: SxProps<Theme> = {
  minWidth: '2.3rem'
};

const headerSx: SxProps<Theme> = {
  fontSize: typography.h4?.fontSize,
  fontWeight: fontWeightBold,
  lineHeight: 'inherit',
  color: 'secondary.main',
  mt: 2,
  mb: 0.75
};

type AppDrawerItemProps = {
  readonly text?: string;
  readonly icon?: ReactNode;
  readonly link?: string;
  readonly href?: string;
  readonly onClick: () => void;
  readonly active: boolean;
}

const AppDrawerItem = forwardRef<HTMLAnchorElement | HTMLDivElement, AppDrawerItemProps>(({
  text,
  icon,
  link,
  href,
  onClick,
  active
}, ref) => {
  const { formatMessage } = useIntl();
  const item = useMemo(() => text ? formatMessage({ id: text }) : undefined, [formatMessage, text]);
  if (!item && !icon) return null;
  const content = link || href ? (
    <>
      {icon ? (
        <ListItemIcon sx={item ? iconSx : undefined}>
          {icon}
        </ListItemIcon>
      ) : undefined}
      {item ? (
        <ListItemText
            primary={item}
            slotProps={slotProps}
        />
      ) : undefined}
    </>
  ) : item;
  return (link && (
    <ListItemButton
        ref={ref as Ref<HTMLAnchorElement>}
        onClick={onClick}
        component={RouterLink}
        to={link}
        selected={active}
    >
      {content}
    </ListItemButton>
  )) || (href && (
    <ListItemButton
        ref={ref as Ref<HTMLAnchorElement>}
        onClick={onClick}
        component="a"
        href={href}
        target={startsWith(href, '/') ? undefined : '_blank'}
        rel="noopener noreferrer"
        selected={active}
    >
      {content}
    </ListItemButton>
  )) || (
    <ListSubheader
        ref={ref as Ref<HTMLLIElement>}
        sx={headerSx}
    >
      {content}
    </ListSubheader>
  );
});

AppDrawerItem.displayName = 'AppDrawerItem';

export default memo(AppDrawerItem);
